import {
  useEffect,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useThrottleFn } from 'ahooks';

function ScrollView(props, ref) {
  const {
    style,
    className,
    scrollX = false,
    scrollY = true,
    scrollbarThin = false,
    enableAutoScroll = false,
    children,
    onScroll,
    offsetHeight = 16,
    id,
    destory,
  } = props;

  const containerRef = useRef(null);
  const scrollingTimer = useRef(0);
  const isAtBottomRef = useRef(false);

  const scrollbarClassName = useMemo(
    () => (scrollbarThin ? 'nw-scrollbar-thin' : 'nw-scrollbar'),
    [scrollbarThin]
  );

  const scrollbarHiddenClassName = useMemo(
    () => (scrollbarThin ? 'nw-scrollbar-thin-hidden' : 'nw-scrollbar-hidden'),
    [scrollbarThin]
  );

  const refCallback = useCallback(instance => {
    containerRef.current = instance;
    if (ref) {
      ref.current = instance;
    }
  }, []);

  const getScrollBottom = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    return scrollHeight - clientHeight - scrollTop;
  }, []);

  const checkIsAtBottom = useCallback(() => {
    isAtBottomRef.current = getScrollBottom() < 1;
  }, []);

  const handleScroll = useCallback(event => {
    const { classList } = event.target;

    const isNearBottom = getScrollBottom() < offsetHeight;
    if (!isNearBottom) {
      classList.remove(scrollbarHiddenClassName);

      window.clearTimeout(scrollingTimer.current);
      scrollingTimer.current = setTimeout(() => {
        classList.add(scrollbarHiddenClassName);
      }, 300);
    }

    checkIsAtBottom();

    onScroll?.(event);
  }, []);

  const autoScrollThrottled = useThrottleFn(
    () => {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    },
    { wait: 300 }
  );

  useEffect(() => {
    if (enableAutoScroll) {
      if (isAtBottomRef.current) {
        autoScrollThrottled.run();
      }
      checkIsAtBottom();
    }
  }, [children, enableAutoScroll]);

  if(destory) return null;

  return (
    <div
      id={id}
      ref={refCallback}
      style={style}
      className={[
        'size-full',
        scrollbarClassName,
        scrollbarHiddenClassName,
        { 'overflow-x-scroll': scrollX, 'overflow-y-scroll': scrollY },
        className,
      ]}
      onWheel={autoScrollThrottled.cancel}
      onTouchMove={autoScrollThrottled.cancel}
      onScroll={handleScroll}
    >
      {children}
    </div>
  );
}

export default forwardRef(ScrollView);
